// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getReviewAndPayCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import { getLocation, getQueryStrings } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getReviewAndPayPromotions } from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import clearMessages from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/ClearMessages";
import loadDataIfNeeded from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/LoadDataIfNeeded";
import notifyProductRecs from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/NotifyProductRecs";
import setCartId from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetCartId";
import setPlaceOrderErrorMessage from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetPlaceOrderErrorMessage";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import AddressErrorModal from "@insite/content-library/Components/AddressErrorModal";
import Page from "@insite/mobius/Page";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import KountDeviceCollector from "@mcmc/App/Components/KountDeviceCollector";
import resetCheckoutCustomState from "@mcmc/App/Handlers/Pages/Checkout/ResetCheckoutCustomState";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapDispatchToProps = {
    setCartId,
    setPlaceOrderErrorMessage,
    loadDataIfNeeded,
    resetCheckoutCustomState,
    clearMessages,
    notifyProductRecs,
};

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = getQueryStrings(state);
    const cartState = getReviewAndPayCartState(state);
    const parsedQuery = parseQueryString<{ cartId?: string; express?: boolean }>(getLocation(state).search);
    const isExpressCheckout = parsedQuery.express;
    const promotionsDataView = getReviewAndPayPromotions(state);
    const expand = state.data.carts.cartApiParameter?.expand || [];

    const isLoadDataNeeded =
        ((!cartState.value ||
            (cartState.value.lineCount && !cartState.value.cartLines?.length) ||
            !cartState.value.carriers ||
            !expand.includes("shipping") ||
            !expand.includes("tax")) &&
            !cartState.isLoading) ||
        (!promotionsDataView.value && !promotionsDataView.isLoading);

    return {
        cartId,
        isExpressCheckout,
        kountSessionId: cartState.value?.properties.kountSessionId || "",
        cartState,
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url || "",
        isLoadDataNeeded: isLoadDataNeeded || !getCurrentCountries(state),
        productRecsCalled: state.pages.checkoutReviewAndSubmit.productRecsCalled,
    };
};

type Props = PageProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps> & HasHistory;

const CheckoutReviewAndSubmitPage = ({
    id,
    cartId,
    cartState, // do not delete this, needed to trigger cart loading
    isLoadDataNeeded,
    productRecsCalled,
    setCartId,
    loadDataIfNeeded,
    setPlaceOrderErrorMessage,
    clearMessages,
    notifyProductRecs,
    history,
    isExpressCheckout,
    cartPageUrl,
    kountSessionId,
}: Props) => {
    const [cartLoaded, setCartLoaded] = useState(false);

    const redirectToCartIfNeeded = () => {
        const { value: cart, isLoading } = cartState;
        if (cart && !isLoading && !cartLoaded) {
            // eslint-disable-next-line react/no-did-update-set-state
            setCartLoaded(true);
            const { lineCount, quoteRequiredCount } = cart;
            let tmpLineCount = 0;
            if (cart.status === "QuoteProposed" || cart.status === "AwaitingApproval" || cart.type === "Quote") {
                tmpLineCount = lineCount;
            } else {
                tmpLineCount = lineCount - quoteRequiredCount;
            }
            if (tmpLineCount <= 0) {
                history.replace(cartPageUrl);
            }
        }
    };

    useEffect(() => {
        setCartId({ cartId });
        loadDataIfNeeded({
            cartId,
            isExpressCheckout,
        } as any);
        setPlaceOrderErrorMessage({});
        clearMessages();
        redirectToCartIfNeeded();
        return () => {
            resetCheckoutCustomState({});
        };
    }, []);

    useEffect(() => {
        if (isLoadDataNeeded) {
            loadDataIfNeeded({ cartId, cartLinesShouldBeExpanded: true });
        } else if (!productRecsCalled) {
            notifyProductRecs({ cartId });
        }
        redirectToCartIfNeeded();
    });

    return (
        <Page data-test-selector="checkoutReviewAndSubmitPage">
            <KountDeviceCollector sessionId={kountSessionId}>
                <Zone zoneName="Content" contentId={id} />
                <AddressErrorModal />
            </KountDeviceCollector>
        </Page>
    );
};

const pageModule: PageModule = {
    component: withHistory(connect(mapStateToProps, mapDispatchToProps)(CheckoutReviewAndSubmitPage)),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "System",
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "CheckoutReviewAndSubmitPage" instead of this constant.
 */
export const CheckoutReviewAndSubmitPageContext = "CheckoutReviewAndSubmitPage";
function useStete(arg0: boolean) {
    throw new Error("Function not implemented.");
}
